import { resolveI18nFields } from './resolveI18nFields'
import type { StatusMatchCampaignConfig, StatusMatchLabPayload } from './statusMatchTypes'
import { isStatusMatchLabPayload } from './statusMatchTypes'

interface LabFlagEntry {
  enabled?: boolean
  value?: unknown
}

function isLabFlagEntry(value: unknown): value is LabFlagEntry {
  return typeof value === 'object' && value !== null
}

function toCampaignConfig(payload: StatusMatchLabPayload): StatusMatchCampaignConfig {
  return { config: payload }
}

/**
 * Validates the raw payload, then resolves `<key>-i18n` siblings into their
 * base keys. Re-validates after resolution and falls back to the English
 * payload if the resolved shape no longer matches.
 */
function resolveCampaignPayload(value: unknown): StatusMatchLabPayload | undefined {
  if (!isStatusMatchLabPayload(value)) return undefined
  const resolved = resolveI18nFields(value)
  return isStatusMatchLabPayload(resolved) ? resolved : value
}

function isCampaignEnabledForRegion(
  payload: StatusMatchLabPayload,
  regionCode: string,
): boolean {
  return payload.allowedRegions.includes(regionCode)
}

/**
 * Resolves status match campaign configs from a Lab feature flag result.
 * Supports legacy single-object payloads and multi-campaign array payloads (same as tier upgrade).
 */
export function resolveStatusMatchCampaignsFromFlag(
  flag: App.LabFeatureFlagResult | undefined,
  regionCode: string,
): Array<StatusMatchCampaignConfig> {
  if (!flag?.enabled) return []

  const { value } = flag

  if (!Array.isArray(value)) {
    const payload = resolveCampaignPayload(value)
    if (!payload) return []
    if (!isCampaignEnabledForRegion(payload, regionCode)) return []
    return [toCampaignConfig(payload)]
  }

  const campaigns: Array<StatusMatchCampaignConfig> = []
  for (const entry of value) {
    if (!isLabFlagEntry(entry) || entry.enabled === false) continue
    const payload = resolveCampaignPayload(entry.value)
    if (!payload) continue
    if (!isCampaignEnabledForRegion(payload, regionCode)) continue
    campaigns.push(toCampaignConfig(payload))
  }

  return campaigns
}

export function resolveStatusMatchCampaignFromFlag(
  flag: App.LabFeatureFlagResult | undefined,
  regionCode: string,
  slug: string,
): StatusMatchCampaignConfig | undefined {
  return resolveStatusMatchCampaignsFromFlag(flag, regionCode).find(
    (campaign) => campaign.config.slug === slug,
  )
}
